type PermissionArray = [string, 'READ' | 'EDIT'][]

/**
 * PERMISSOES E ACESSOS
 * cada chave contem um array que descreve uma funcionalidade ou navegacao que cada
 * tipo de acesso permite, e se esse acesso é de visualizacao ou tambem edicao.
 *
 * [
 *  string, - acesso a funcionalidade
 *  'READ' | 'EDIT' - tipo de permissao da funcionalidade
 * ]
 *
 * READ: funcionalidade de visualizacao, ela nao tem interacao direta com usuario.
 * (acesso a uma pagina, a um modal de detalhe, visualizar documento, etc).
 * EDIT: a funcionalidade possui algum tipo de interacao com o usuario.
 * (Botao, acesso a formulario, edicao de informacao, upload de documento, etc).
 *
 *
 * ADICIONAR NOVO NIVEL DE ACESSO:
 * Criar uma nova chave no objeto "acessPermissions"
 * e um array de funcionalidades e os tipos de permissao.
 *
 * ../utils.ts ---> adicionar acesso em optionsLevels
 * ../../colaboradores  ---> atualizar arquivos de colaboradores (novoColaborador, resumoColaborador, factory)
 *
 * ADICIONAR NOVO ACESSO:
 * Adicionar novo array de funcionalidade no value do nivel de acesso escolhido.
 *
 * @interface AccessLib
 * @typedef {AccessLib}
 */
interface AccessLib {
  SPECIALIST: PermissionArray // Acesso tipo "Especialista"
  SPECGESTOR: PermissionArray // Acesso tipo "Gestor e Especialista"
  DIRECTOR: PermissionArray // Acesso tipo "Diretor"
  GESTOR: PermissionArray // Acesso tipo "Gestor"
  LEGAL: PermissionArray // Acesso tipo "Juridico"
  RI: PermissionArray // Acesso tipo "Relacionamento com investidor"
  RH: PermissionArray // Acesso tipo "Gestor de usuários"
  PAYANALYST: PermissionArray // Acesso tipo "Analista de pagamentos"
  CONTRACTANALYST: PermissionArray // Acesso tipo "Analista de contratos"
  FINANCIAL: PermissionArray // Acesso tipo "Financeiro"
  OWNER: PermissionArray // Acesso tipo "Owner"
}

const specialistPermission: PermissionArray = [
  ['dashboard', 'READ'], // enable do "Menu" ao dashboard
  ['clients', 'READ'], // enable do "Menu" a gestao de clientes
  ['new-client-button', 'EDIT'], // Botao do form de novo cliente
  ['new-captation-button', 'EDIT'], // Botao do form de nova captacao
  ['gestao-clients-toggle', 'READ'], // enable do toggle de "Gestao de conta"
  ['fluxo-comercial-toggle', 'READ'], // enable do toggle de "Fluxo comercial"
  ['fluxo-contrato-toggle', 'READ'], // enable do toggle de "Fluxo de contrato"
  ['proposta-personal-toggle', 'EDIT'], // enable do toggle de "Gestao de conta"
  ['trash-remove-proposta', 'EDIT'], // interacao com botao de lixeira no "Kanban Card"
  ['editar-card-info-cliente-name', 'EDIT'], // card editavel de nome do cliente
  ['editar-card-info-cliente-rg', 'EDIT'], // card editavel de RG do cliente
  ['editar-card-info-cliente-birthdate', 'EDIT'], // card editavel de data de nascimento do cliente
  ['editar-card-info-cliente-cellphone', 'EDIT'], // card editavel de celular do cliente
  ['editar-card-info-cliente-email', 'EDIT'], // card editavel de email do cliente
  ['editar-card-info-cliente-civilState', 'EDIT'], // card editavel de estado civil do cliente
  ['editar-card-info-cliente-ciaType', 'EDIT'], // card editavel de tipo de empresa do cliente
  ['editar-card-info-cliente-cep', 'EDIT'], // card editavel de cep do cliente
  ['editar-card-info-cliente-province', 'EDIT'], // card editavel de bairro do cliente
  ['editar-card-info-cliente-address', 'EDIT'], // card editavel de rua do cliente
  ['editar-card-info-cliente-number', 'EDIT'], // card editavel de numero do cliente
  ['editar-card-info-cliente-complement', 'EDIT'], // card editavel de complemento do cliente
  ['editar-card-info-cliente-bank', 'EDIT'], // card editavel de banco do cliente
  ['editar-card-info-cliente-pixType', 'EDIT'], // card editavel de tipo de pix do cliente
  ['editar-card-info-cliente-pixKey', 'EDIT'], // card editavel de chave pix do cliente
  ['analisar-documento-checkbox', 'EDIT'], // checkbox "Analisar documento"
  ['analisar-campos-checkbox', 'EDIT'], // checkbox "Analisar campos"
  ['upload-document-client', 'EDIT'], // input de arquivo, para subir foto do documento do cliente
  ['wallet-remove-captation', 'EDIT'], // lixeira de excluir captacao no modal de detalhe do produto, na carteira do cliente
  ['wallet-add-documents', 'EDIT'], // input de arquivo, para subir documento ou contrato da captacao no modal de detalhe de produto, na carteiro do cliente
  ['detalhe-contract-add-documents', 'EDIT'], // input de arquivo, para subir documento ou contrato da captacao no modal de detalhe da proposta, no card kanban do fluxo comercial
  ['copy-pix-key', 'EDIT'], // botao de "Copiar chave pix"
  ['migration', 'EDIT'], // acesso ao formulario de "Migracao" do contrato
]

const directorPermission: PermissionArray = [
  ...specialistPermission,
  ['select-client-specialist', 'EDIT'], // para mudar o especialista do cliente
  ['products', 'READ'], // acesso do "Menu" a gestao de produtos
  ['filter-specialist', 'READ'], // acesso ao filtro de especialistas
  ['filter-multi-specialist', 'READ'], // acesso ao filtro de especialistas e seus agrupamentos

  ['fluxo-juridico-toggle', 'READ'], // enable do toggle de "Fluxo juridico"
  ['fluxo-saque-toggle', 'READ'], // enable do toggle de "Fluxo de saque"
  ['detalhe-juridico-add-documents', 'EDIT'], // input de arquivo, para subir documento ou contrato da captacao no modal de detalhe da proposta, no card kanban do fluxo juridico
  ['new-enterprise-button', 'EDIT'], // Botao para form de "Novo empreendimento"
  ['products-kanban-draggable', 'EDIT'], // enable mover card no Kanban de captacao, na gestao de produtos
  ['add-document-enterprise', 'EDIT'], // input de arquivo, para subir documento ou contrato do empreendimento,
  ['edit-enterprise-name', 'EDIT'], // card editavel de nome do empreendimento
  ['edit-enterprise-shortName', 'EDIT'], // card editavel de nome resumido do empreendimento
  ['edit-enterprise-amount', 'EDIT'], // card editavel de quantidade de cotas do empreendimento
  ['edit-enterprise-value', 'EDIT'], // card editavel de preco da cota do empreendimento
  ['edit-enterprise-quotaValue', 'EDIT'], // card editavel de nome do empreendimento
  ['add-document-captation', 'EDIT'], // input de arquivo, para subir documento ou contrato do empreendimento, no modal de detalhe do card do kanban de captacao, em gestao de produtos
  ['edit-valorDoInvestimento-financial', 'EDIT'], // enable o card editavel do "Valor de investimento"

  ['gestors', 'READ'],
  ['change-gestors-info', 'EDIT'],
  ['delete-gestors', 'EDIT'],
  ['edit-dates-financial', 'EDIT'],
  ['create-new-gestors', 'EDIT'], // botao de acesso ao formulario de "Novo Colaborador"
]

const paymentAnalystPermissions: PermissionArray = [
  ['dashboard', 'READ'],
  ['clients', 'READ'],
  ['financial', 'READ'], // acesso do "Menu" a gestao financeira
  ['filter-specialist', 'READ'],
  ['filter-multi-specialist', 'READ'],
  ['fluxo-comercial-toggle', 'READ'],
  ['fluxo-contrato-toggle', 'READ'],
  ['gestao-clients-toggle', 'READ'],
  ['fluxo-saque-toggle', 'READ'],
  ['editar-card-info-cliente-bank', 'EDIT'],
  ['editar-card-info-cliente-pixType', 'EDIT'],
  ['editar-card-info-cliente-pixKey', 'EDIT'],
  ['wallet-remove-captation', 'EDIT'],
  ['wallet-add-documents', 'EDIT'],
  ['detalhe-contract-add-documents', 'EDIT'],
  ['copy-pix-key', 'EDIT'],
  ['migration', 'EDIT'],
]

const contractAnalystPermissions: PermissionArray = [
  ...paymentAnalystPermissions,
  ['trash-remove-proposta', 'EDIT'],
  ['edit-dates-financial', 'EDIT'], // enable o card editavel do "Data do investimeto" e "Inicio do pagamento", em gestao financeira
]

const acessPermissions: AccessLib = {
  SPECIALIST: specialistPermission,
  SPECGESTOR: [
    ...specialistPermission,
    ['select-client-specialist', 'EDIT'],
    ['fluxo-saque-toggle', 'READ'],
    ['products', 'READ'],
    ['filter-specialist', 'READ'],
    ['filter-multi-specialist', 'READ'],
    ['edit-valorDoInvestimento-financial', 'EDIT'], // enable o card editavel do "Valor de investimento"
    ['edit-dates-financial', 'EDIT'],
  ],
  DIRECTOR: directorPermission,
  GESTOR: [
    ...specialistPermission,
    ['select-client-specialist', 'EDIT'],
    ['fluxo-saque-toggle', 'READ'],
    ['products', 'READ'],
    ['filter-specialist', 'READ'],
    ['filter-multi-specialist', 'READ'],
    ['edit-valorDoInvestimento-financial', 'EDIT'], // enable o card editavel do "Valor de investimento"
    ['edit-dates-financial', 'EDIT'],
  ],
  LEGAL: [
    ['dashboard', 'READ'],
    ['clients', 'READ'],
    ['detalhe-contract-add-documents', 'EDIT'],
    ['detalhe-juridico-add-documents', 'EDIT'],
    ['fluxo-juridico-toggle', 'READ'],
    ['fluxo-contrato-toggle', 'READ'],
    ['filter-specialist', 'READ'],
    ['filter-multi-specialist', 'READ'],
  ],
  RI: specialistPermission,
  RH: [
    ['dashboard', 'READ'],
    ['gestors', 'READ'], // acesso do "Menu" a gestao de usuarios
    ['change-gestors-info', 'EDIT'], // edicao de informacoes dos colaboradores na tabela
    ['delete-gestors', 'EDIT'], // lixeira para excluir na tabela
    ['create-new-gestors', 'EDIT'], // botao de acesso ao formulario de "Novo Colaborador"
  ],
  PAYANALYST: paymentAnalystPermissions,
  CONTRACTANALYST: contractAnalystPermissions,
  FINANCIAL: [
    ...contractAnalystPermissions,
    ...paymentAnalystPermissions,

    ['trash-remove-proposta', 'EDIT'],
    ['depositar-financial', 'EDIT'], // Botao de "+Transferir" em gestao financeira
    ['transfer-financial', 'EDIT'], // Botao de "+Depositar" em gestao financeira
    ['checkbox-pagamentoManual-financial', 'EDIT'], // checkbox de "Pagamento manual", no modal de detalhe do kanban card em gestao financeira
    ['checkbox-analisePagamento-financial', 'EDIT'], // checkbox de "Analise de pagamento", no modal de detalhe do kanban card em gestao financeira
    ['checkbox-analiseContrato-financial', 'EDIT'], // checkbox de "Analise de contrato", no modal de detalhe do kanban card em gestao financeira
    ['checkbox-payNow-financial', 'EDIT'], // checkbox de "Pagar Agora",  no modal de detalhe do kanban card em gestao financeira
    ['edit-valorDoInvestimento-financial', 'EDIT'], // enable o card editavel do "Valor de investimento"
  ],
  OWNER: [
    ...specialistPermission,
    ...directorPermission,
    ['gestors', 'READ'],
    ['change-gestors-info', 'EDIT'],
    ['delete-gestors', 'EDIT'],
    ['create-new-gestors', 'EDIT'],
    ...paymentAnalystPermissions,
    ...contractAnalystPermissions,
    ['depositar-financial', 'EDIT'],
    ['transfer-financial', 'EDIT'],
    ['checkbox-pagamentoManual-financial', 'EDIT'],
    ['checkbox-analisePagamento-financial', 'EDIT'],
    ['checkbox-analiseContrato-financial', 'EDIT'],
    ['edit-valorDoInvestimento-financial', 'EDIT'],
    ['checkbox-payNow-financial', 'EDIT'],
  ],
}
export default acessPermissions
